// Vue camelises a host attribute into a prop key, then sets it on a forwarding target as a
// property when one exists and as an attribute under the camelised name when it does not. An
// attribute name the DOM then lowercases — `ariaHaspopup` becomes `ariahaspopup` — is inert.
const CAMELISED_ARIA = /^aria[A-Z]/;

// No ARIA attribute carries an internal hyphen, so everything after `aria` is one lowercase token.
// That holds however the key was capitalised, which is what makes the attribute spelling Vue
// produces (`ariaHaspopup`) and the IDL spelling (`ariaHasPopup`) land on the same name.
function toAriaAttribute(key) {
  return `aria-${key.slice("aria".length).toLowerCase()}`;
}

/**
 * Returns `attrs` with every camelised `aria*` key restored to its hyphenated attribute name, so
 * a forwarded ARIA attribute reaches the target under the name it was written as:
 * `{ ariaHaspopup: "listbox" }` → `{ "aria-haspopup": "listbox" }`.
 *
 * Both the attribute spelling Vue produces and the IDL spelling resolve to the same name, so
 * `ariaHaspopup` and `ariaHasPopup` both give `aria-haspopup`.
 *
 * Every other key is passed through untouched, `data*` included: a `data-*` attribute is not
 * forwarded into shadow DOM, so that a `data-testid` on the host stays a single match.
 *
 * @param {Record<string, unknown>} attrs
 * @returns {Record<string, unknown>}
 */
export function withHyphenatedAttrs(attrs) {
  return Object.fromEntries(
    Object.entries(attrs).map(([key, value]) => [CAMELISED_ARIA.test(key) ? toAriaAttribute(key) : key, value]),
  );
}

const DATA_KEY = /^data-|^data[A-Z]/;

/**
 * Returns the attributes a component should forward onto the control inside its shadow root, for
 * a component that has turned Vue's own fallthrough off.
 *
 * Evaluate it per render rather than caching it: Vue's `attrs` proxy tracks a `get`, so a map that
 * is empty at mount registers no dependency and a cached reader never re-runs. The component
 * re-renders on every attrs change, so a plain call is always current.
 *
 * `data-*` is not forwarded. The framework marks every host with `data-sp-ce-ready`, and a
 * `data-testid` must stay a single match for the QA suite, which pierces shadow DOM.
 *
 * @param {Record<string, unknown>} attrs
 * @returns {Record<string, unknown>}
 */
export function forwardableAttrs(attrs) {
  return Object.fromEntries(Object.entries(withHyphenatedAttrs(attrs)).filter(([key]) => !DATA_KEY.test(key)));
}
